import { Injectable } from '@angular/core';
import { Action, State, StateContext } from '@ngxs/store';
import { tap, catchError } from 'rxjs/operators';
import { of, throwError, firstValueFrom } from 'rxjs';
import {
  CgvAcceptanceHistoryService,
  CgvAcceptanceService,
  CgvVersionEntity,
  CgvVersionsService,
  FilterCgvResponse,
  FilterCgvVersionResponse,
} from '@smatchy/sdk';
import {
  LoadCgvList,
  LoadCgvListSuccess,
  LoadCgvListFail,
  LoadLastCgvVersionSuccess,
  LoadLastCgvVersion,
  LoadLastCgvVersionFail,
  AcceptedNewCgv,
  AcceptedNewCgvSuccess,
  AcceptedNewRefund,
  AcceptedNewGcs,
  AcceptedNewLegalText,
  CheckNewCgvVersion,
  CheckNewCgvVersionSuccess,
  CheckNewCgvVersionFail,
} from './cgv.actions';
import { CgvService } from '@smatchy/sdk';
import { theBodyOf } from '../../../utils/what-the-api-returns';

export interface CgvStateModel {
  list: FilterCgvResponse | null;
  versions: FilterCgvVersionResponse | null;

  lastVersion: CgvVersionEntity;

  versionDetail: CgvVersionEntity | null;
  loading: boolean;
  error?: unknown;
  newVersion: boolean;
  newCommissionVersion: boolean;
  newGcsVersion: boolean;
  newCguVersion: boolean;
  newPrivacyVersion: boolean;
}

@State<CgvStateModel>({
  name: 'cgv',
  defaults: {
    list: null,
    versions: null,
    lastVersion: {},
    versionDetail: null,
    loading: false,
    newVersion: false,
    newCommissionVersion: false,
    newGcsVersion: false,
    newCguVersion: false,
    newPrivacyVersion: false,
  },
})
@Injectable()
export class CgvState {
  constructor(
    private cgvService: CgvService,
    private cgvVersionService: CgvVersionsService,
    private cgvAcceptanceService: CgvAcceptanceService,
    private cgvAcceptanceHistoryService: CgvAcceptanceHistoryService,
  ) {}

  @Action(LoadCgvList)
  load(ctx: StateContext<CgvStateModel>, action: LoadCgvList) {
    ctx.patchState({ loading: true, error: null });

    return this.cgvService.filterCgv(action.params).pipe(
      tap((res) => {
        ctx.dispatch(new LoadCgvListSuccess(res as FilterCgvResponse));
      }),
      catchError((error) => {
        ctx.dispatch(new LoadCgvListFail(error));
        return of(error);
      }),
    );
  }

  @Action(LoadCgvListSuccess)
  loadCgvListSuccess(ctx: StateContext<CgvStateModel>, action: LoadCgvListSuccess) {
    ctx.patchState({
      list: action.response,
      loading: false,
    });

    const cgvs = action.response.cgvs ?? [];
    // A type predicate on the filter, not a plain boolean: `Array.filter` with
    // a boolean keeps the element type it started with, so `_id` was still
    // optional in the map below even though the filter exists to guarantee it.
    return ctx.dispatch(
      cgvs
        .filter((cgv): cgv is (typeof cgvs)[number] & { _id: string } => !!cgv._id)
        .map((cgv) => new LoadLastCgvVersion(cgv._id)),
    );
  }

  @Action(LoadCgvListFail)
  loadFail(ctx: StateContext<CgvStateModel>, action: LoadCgvListFail) {
    ctx.patchState({
      loading: false,
      error: action.error,
    });
  }
  /* ===== LAST VERSION ===== */
  @Action(LoadLastCgvVersion)
  loadLastVersion(ctx: StateContext<CgvStateModel>, action: LoadLastCgvVersion) {
    ctx.patchState({ loading: true, error: null });

    return this.cgvVersionService.findLastVersion({ id: action.cgvId }).pipe(
      tap((res) =>
        // The response of this endpoint carries the version under `response`,
        // and `theBodyOf` types it `unknown` because no contract describes it.
        // Named here, at the one place it is read.
        ctx.dispatch(new LoadLastCgvVersionSuccess(theBodyOf<{ response?: CgvVersionEntity }>(res)?.response)),
      ),
      catchError((err) => {
        ctx.dispatch(new LoadLastCgvVersionFail(err));
        return of(err);
      }),
    );
  }

  @Action(LoadLastCgvVersionSuccess)
  loadLastVersionSuccess(ctx: StateContext<CgvStateModel>, action: LoadLastCgvVersionSuccess) {
    ctx.patchState({ lastVersion: action.version, loading: false });
  }

  @Action(LoadLastCgvVersionFail)
  loadLastVersionFail(ctx: StateContext<CgvStateModel>, action: LoadLastCgvVersionFail) {
    ctx.patchState({ loading: false, error: action.error });
  }
  // @Action(LoadLastCgvVersionSuccess)
  // loadLastVersionSuccess(ctx: StateContext<CgvStateModel>, action: LoadLastCgvVersionSuccess) {
  //   const state = ctx.getState();
  //   console.log('state:', state);
  //   ctx.patchState({
  //     lastVersions: {
  //       ...state.lastVersions,
  //       [action.version.cgv._id]: action.version,
  //     },
  //   });
  // }
  @Action(AcceptedNewCgv)
  async AcceptedNewCgv(ctx: StateContext<CgvStateModel>, action: AcceptedNewCgv) {
    ctx.patchState({ loading: true, error: null });

    try {
      const res = await this.cgvAcceptanceHistoryService
        .accept({
          updateCgvAcceptanceHistoryByCgvAcceptanceDto: {
            userId: action.userId,
            type: 'commission',
          },
        })
        .pipe(
          tap((res) => {
            console.log(res);
          }),
          catchError((err: unknown) => {
            ctx.dispatch({
              error: err,
            });
            return throwError(() => err);
          }),
        );

      ctx.patchState({
        loading: false,
      });
      return res;
    } catch (error) {
      ctx.patchState({
        loading: false,
        error,
      });
      console.log('AcceptedNewCgv', error);
    } finally {
      ctx.patchState({
        loading: false,
      });
    }
  }
  @Action(AcceptedNewRefund)
  async AcceptedNewRefund(ctx: StateContext<CgvStateModel>, action: AcceptedNewRefund) {
    ctx.patchState({ loading: true, error: null });

    try {
      console.log(action);
      const res = await this.cgvAcceptanceHistoryService
        .accept({
          updateCgvAcceptanceHistoryByCgvAcceptanceDto: {
            userId: action.userId,
            type: 'refund',
          },
        })
        .pipe(
          tap((res) => {
            console.log(res);
          }),
          catchError((err: unknown) => {
            ctx.dispatch({
              error: err,
            });
            return throwError(() => err);
          }),
        );

      ctx.patchState({
        loading: false,
      });
      return res;
    } catch (error) {
      ctx.patchState({
        loading: false,
        error,
      });
      console.log('AcceptedNewCgv', error);
    } finally {
      ctx.patchState({
        loading: false,
      });
    }
  }

  @Action(AcceptedNewGcs)
  async AcceptedNewGcs(ctx: StateContext<CgvStateModel>, action: AcceptedNewGcs) {
    ctx.patchState({ loading: true, error: null });

    try {
      // Ensure the acceptance record exists for "gcs", then accept the latest version history.
      await firstValueFrom(
        this.cgvAcceptanceService.createCgvAcceptance({
          createCgvAcceptanceDto: {
            type: 'gcs',
          },
        }),
      );

      const res = await firstValueFrom(
        this.cgvAcceptanceHistoryService.accept({
          updateCgvAcceptanceHistoryByCgvAcceptanceDto: {
            userId: action.userId,
            type: 'gcs',
          },
        }),
      );

      ctx.patchState({ loading: false });
      return res;
    } catch (error) {
      ctx.patchState({ loading: false, error });
      throw error;
    }
  }
  /*
   * No createCgvAcceptance first, unlike the sale conditions: the accept route
   * creates the row itself when there is none, and nobody has one for these
   * two texts yet.
   */
  @Action(AcceptedNewLegalText)
  async acceptedNewLegalText(ctx: StateContext<CgvStateModel>, action: AcceptedNewLegalText) {
    ctx.patchState({ loading: true, error: null });
    try {
      const res = await firstValueFrom(
        this.cgvAcceptanceHistoryService.accept({
          updateCgvAcceptanceHistoryByCgvAcceptanceDto: { userId: action.userId, type: action.text },
        }),
      );
      ctx.patchState({
        loading: false,
        ...(action.text === 'cgu' ? { newCguVersion: false } : { newPrivacyVersion: false }),
      });
      return res;
    } catch (error) {
      ctx.patchState({ loading: false, error });
      throw error;
    }
  }

  /* ===== CHECK NEW VERSION ===== */
  @Action(CheckNewCgvVersion)
  checkNewVersion(ctx: StateContext<CgvStateModel>) {
    ctx.patchState({ loading: true, error: null });

    return this.cgvAcceptanceService.checkNewCgv().pipe(
      tap((res) => ctx.dispatch(new CheckNewCgvVersionSuccess(theBodyOf(res)))),
      catchError((err) => {
        ctx.dispatch(new CheckNewCgvVersionFail(err));
        return of(err);
      }),
    );
  }

  @Action(CheckNewCgvVersionSuccess)
  checkNewVersionSuccess(ctx: StateContext<CgvStateModel>, action: CheckNewCgvVersionSuccess) {
    // An answer with no body means nothing new to accept, which is what all
    // three flags already say when they are false. Read once into a local
    // rather than four times through an optional chain.
    const answered = action.data;

    ctx.patchState({
      newVersion: answered?.data ?? false,
      newCommissionVersion: answered?.commission ?? answered?.data ?? false,
      newGcsVersion: answered?.gcs ?? false,
      newCguVersion: answered?.cgu === true,
      newPrivacyVersion: answered?.privacy === true,
      loading: false,
    });
  }

  @Action(CheckNewCgvVersionFail)
  checkNewVersionFail(ctx: StateContext<CgvStateModel>, action: CheckNewCgvVersionFail) {
    ctx.patchState({ loading: false, error: action.error });
  }
}
